import _ from 'underscore';

import {assert} from 'Asserts';
import {DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import * as util from 'biojs/Util';
import {CRISPRGuide} from 'constants/CRISPRConstants';
import * as CRISPRUtil from 'molbio/crispr/CRISPRUtil';
import * as sequtil from 'molbio/SeqUtil';

// Adapted from https://raw.githubusercontent.com/jbsimorg/crispor/master/doenchScore.py
const PARAMS: Array<[number, string, number]> = [
  [1, 'G', -0.2753771],
  [2, 'A', -0.3238875],
  [2, 'C', 0.17212887],
  [3, 'C', -0.1006662],
  [4, 'C', -0.2018029],
  [4, 'G', 0.24595663],
  [5, 'A', 0.03644004],
  [5, 'C', 0.09837684],
  [6, 'C', -0.7411813],
  [6, 'G', -0.3932644],
  [11, 'A', -0.466099],
  [14, 'A', 0.08537695],
  [14, 'C', -0.013814],
  [15, 'A', 0.27262051],
  [15, 'C', -0.1190226],
  [15, 'T', -0.2859442],
  [16, 'A', 0.09745459],
  [16, 'G', -0.1755462],
  [17, 'C', -0.3457955],
  [17, 'G', -0.6780964],
  [18, 'A', 0.22508903],
  [18, 'C', -0.5077941],
  [19, 'G', -0.4173736],
  [19, 'T', -0.054307],
  [20, 'G', 0.37989937],
  [20, 'T', -0.0907126],
  [21, 'C', 0.05782332],
  [21, 'T', -0.5305673],
  [22, 'T', -0.8770074],
  [23, 'C', -0.8762358],
  [23, 'G', 0.27891626],
  [23, 'T', -0.4031022],
  [24, 'A', -0.0773007],
  [24, 'C', 0.28793562],
  [24, 'T', -0.2216372],
  [27, 'G', -0.6890167],
  [27, 'T', 0.11787758],
  [28, 'C', -0.1604453],
  [29, 'G', 0.38634258],
  [1, 'GT', -0.6257787],
  [4, 'GC', 0.30004332],
  [5, 'AA', -0.8348362],
  [5, 'TA', 0.76062777],
  [6, 'GG', -0.4908167],
  [11, 'GG', -1.5169074],
  [11, 'TA', 0.7092612],
  [11, 'TC', 0.49629861],
  [11, 'TT', -0.5868739],
  [12, 'GG', -0.3345637],
  [13, 'GA', 0.76384993],
  [13, 'GC', -0.5370252],
  [16, 'TG', -0.7981461],
  [18, 'GG', -0.6668087],
  [18, 'TC', 0.35318325],
  [19, 'CC', 0.74807209],
  [19, 'TG', -0.3672668],
  [20, 'AC', 0.56820913],
  [20, 'CG', 0.32907207],
  [20, 'GA', -0.8364568],
  [20, 'GG', -0.7822076],
  [21, 'TC', -1.029693],
  [22, 'CG', 0.85619782],
  [22, 'CT', -0.4632077],
  [23, 'AA', -0.5794924],
  [23, 'AG', 0.64907554],
  [24, 'AG', -0.0773007],
  [24, 'CG', 0.28793562],
  [24, 'TG', -0.2216372],
  [26, 'GT', 0.11787758],
  [28, 'GG', -0.69774],
];

// guide length required by Doench algorithm
const DOENCH_GUIDE_SIZE = 20;
// bp before and after guide used by Doench
const DOENCH_UPSTREAM_OFFSET = 4;
const DOENCH_DOWNSTREAM_OFFSET = 6;
const DOENCH_INPUT_SIZE = DOENCH_GUIDE_SIZE + DOENCH_UPSTREAM_OFFSET + DOENCH_DOWNSTREAM_OFFSET;

const INTERCEPT = 0.59763615;
const GC_HIGH = -0.1665878;
const GC_LOW = -0.2026259;

/**
 * Global object of util functions for calculating on-target scores for CRISPR guides
 */
const doenchScore = {
  _calcDoenchScore: (seq: string): number => {
    // Sync with benchling/lib/doench_score.py::_calc_doench_score
    let gcWeight;
    seq = seq.toUpperCase();
    assert(!util.isDegenerate(seq), 'Found unexpected degenerate bases while calculating Doench score');
    let score = INTERCEPT;

    const guideSeq = seq.slice(4, 24);
    const counts = util.count(guideSeq, util.NucleotideType.DNA);
    const gcCount = counts.G + counts.C;
    if (gcCount <= 10) {
      gcWeight = GC_LOW;
    } else {
      gcWeight = GC_HIGH;
    }
    score += Math.abs(10 - gcCount) * gcWeight;

    for (const [pos, modelSeq, weight] of PARAMS) {
      const subSeq = seq.slice(pos, pos + modelSeq.length);
      if (subSeq === modelSeq) {
        score += weight;
      }
    }
    return 1.0 / (1.0 + Math.exp(-score));
  },
  /**
   * Normalize and return a copy of the guide object with "bases" overwritten to be normalized to it's
   * {DOENCH_GUIDE_SIZE}-bp equivalent form. The returned guide is mutable and points to the same state
   * as the provided guide argument.
   * @param  {Object} guide The original guide object.
   * @return {Object}       A normalized guide object whose attributes point to those of the original guide
   * object other than "bases" which is a copy such that bases.length <= DOENCH_GUIDE_SIZE.
   */
  _getNormalizedGuide: (guide: CRISPRGuide): CRISPRGuide => {
    // Sync with benchling/lib/doench_score.py::get_normalized_guide
    if (guide.bases.length <= DOENCH_GUIDE_SIZE) {
      return guide;
    }
    if (guide.strand === 1) {
      // eslint-disable-next-line no-restricted-syntax
      return _.extend({}, guide, {bases: guide.bases.slice(0, DOENCH_GUIDE_SIZE)});
    } else {
      const guideStart = guide.bases.length - DOENCH_GUIDE_SIZE;
      // eslint-disable-next-line no-restricted-syntax
      return _.extend({}, guide, {bases: guide.bases.slice(guideStart)});
    }
  },
  getScoringRegionForDoenchScore: (guide: CRISPRGuide, circular: boolean, bases: string): string | null => {
    // Sync with benchling/lib/doench_score.py::get_scoring_region_for_doench_score
    let start;
    let end;
    const sequenceLength = bases.length;
    const normalizedGuide = doenchScore._getNormalizedGuide(guide);
    if (sequenceLength < DOENCH_INPUT_SIZE) {
      return null;
    }
    if (normalizedGuide.strand === 1) {
      start = CRISPRUtil.guideStart(normalizedGuide, sequenceLength, false) - DOENCH_UPSTREAM_OFFSET;
      end = CRISPRUtil.guideEnd(normalizedGuide, sequenceLength, false) + DOENCH_DOWNSTREAM_OFFSET;
    } else {
      start = CRISPRUtil.guideStart(normalizedGuide, sequenceLength, false) - DOENCH_DOWNSTREAM_OFFSET;
      end = CRISPRUtil.guideEnd(normalizedGuide, sequenceLength, false) + DOENCH_UPSTREAM_OFFSET;
    }

    start = sequtil.mod(start, sequenceLength);
    end = sequtil.mod(end, sequenceLength);
    if (!sequtil.isValidRange(start, end, sequenceLength, circular)) {
      return null;
    }

    return util.slice(bases, start, end, circular);
  },
  doenchScore: (guide: CRISPRGuide, circular: boolean, bases: string): number | null => {
    // Sync with benchling/lib/doench_score.py::doench_score
    let scoringRegion: string | null = doenchScore.getScoringRegionForDoenchScore(guide, circular, bases);
    if (scoringRegion == null) {
      return null;
    }
    if (scoringRegion.length !== DOENCH_INPUT_SIZE) {
      return null;
    }
    if (util.isDegenerate(scoringRegion)) {
      return null;
    }
    if (guide.strand === -1) {
      scoringRegion = util.revcomp(scoringRegion, DNA_COMPLEMENT_TABLE);
    }
    return 100 * doenchScore._calcDoenchScore(scoringRegion);
  },
  // eslint-disable-next-line object-shorthand
  DOENCH_GUIDE_SIZE: DOENCH_GUIDE_SIZE,
};

// Private methods are exported for testing
export default doenchScore;
